import { createApi } from '@reduxjs/toolkit/query/react';

import { TrainingActivityTypes } from '../endpoints';
import {
  getBaseQuery,
  getBaseQueryError,
  getListProvidesTags,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import {
  HTTP_METHOD,
  REDUX_CACHE,
  TRAINING_ACTIVITY_TYPES,
} from '../constants';
import {
  ICreateTrainingActivityTypeBody,
  ITrainingActivityType,
  IUpdateTrainingActivityTypeBody,
} from '../../../types/interfaces/training-activity-type';
import {
  errorToastMessage,
  successToastMessage,
} from '../../../components/layout/toast-root/toast';
import { Messages } from '../../../shared/messages';

export const trainingActivityTypesApi = createApi({
  reducerPath: 'trainingActivityTypesApi',
  baseQuery: getBaseQuery(),
  tagTypes: [
    TRAINING_ACTIVITY_TYPES.TRAINING_ACTIVITY_TYPES,
    TRAINING_ACTIVITY_TYPES.TRAINING_ACTIVITY_TYPE,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getTrainingActivityTypesApi: builder.query<ITrainingActivityType[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: TrainingActivityTypes.ROOT,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(
          result,
          TRAINING_ACTIVITY_TYPES.TRAINING_ACTIVITY_TYPES,
        ),
      ...TransformedErrorResponseWithNotification(),
    }),
    getTrainingActivityTypeByIdApi: builder.query<
      ITrainingActivityType,
      string
    >({
      query(id) {
        return {
          url: TrainingActivityTypes.BY_ID(id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (_result, _error, id) => [
        { type: TRAINING_ACTIVITY_TYPES.TRAINING_ACTIVITY_TYPE, id },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutTrainingActivityTypeApi: builder.mutation<
      void,
      {
        id?: string;
        body: ICreateTrainingActivityTypeBody | IUpdateTrainingActivityTypeBody;
      }
    >({
      query({ id, body }) {
        return {
          url: id
            ? TrainingActivityTypes.BY_ID(id)
            : TrainingActivityTypes.ROOT,
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      transformResponse: (response: any, meta: any): any => {
        let message = '';
        const method = meta.request.method;

        switch (method) {
          case HTTP_METHOD.POST:
            message = `Training activity type ${Messages.dataCreated}`;
            break;
          case HTTP_METHOD.PUT:
            message = `Training activity type ${Messages.dataUpdated}`;
            break;
        }

        successToastMessage(message);
        return { id: response?.id, status: meta?.response?.status };
      },
      transformErrorResponse: (response: any, meta: any) => {
        const error = getBaseQueryError(response);

        if (meta?.response?.status === 400) {
          return {
            message: error?.message,
            status: meta?.response?.status,
            data: {
              message: error?.message,
            },
          };
        }

        errorToastMessage(error?.message);

        return { message: error?.message, status: meta?.response?.status };
      },
      invalidatesTags: (_result, error) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }

        return [
          {
            type: TRAINING_ACTIVITY_TYPES.TRAINING_ACTIVITY_TYPES,
            id: REDUX_CACHE.LIST,
          },
          {
            type: TRAINING_ACTIVITY_TYPES.TRAINING_ACTIVITY_TYPE,
          },
        ];
      },
    }),
    deleteTrainingActivityTypeApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: TrainingActivityTypes.BY_ID(id),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (_result, error) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        }

        return [
          {
            type: TRAINING_ACTIVITY_TYPES.TRAINING_ACTIVITY_TYPES,
            id: REDUX_CACHE.LIST,
          },
        ];
      },
      ...TransformedResponseWithNotification('Training activity type'),
    }),
  }),
});

export const {
  useGetTrainingActivityTypesApiQuery: useGetTrainingActivityTypes,
  useGetTrainingActivityTypeByIdApiQuery: useGetTrainingActivityTypeById,
  usePostPutTrainingActivityTypeApiMutation: usePostPutTrainingActivityType,
  useDeleteTrainingActivityTypeApiMutation: useDeleteTrainingActivityType,
} = trainingActivityTypesApi;
